// ============================================================
// 旧SPA操作防止（U-6 / docs/design_stale_spa_guard.md）ランタイム層
//
// Codex 実装の純粋層 appVersionCore.ts / ブラウザ wrapper appVersion.ts の
// 上に載るアプリ状態ストア。/app-version.json の現行 build id と自分の
// build id を比較し、
//   - 読み取り中: stale なら穏やかな更新バナーを出す（StaleAppVersionBoundary）
//   - 書き込み前: stale なら操作を止めてブロックモーダルを出し更新を要求する
// という二段構えの「受け口」を提供する。
//
// dev / build id 未設定（CURRENT_APP_BUILD_ID === null）では完全 no-op。
// 通信失敗は unknown 扱いで、書き込みを過剰に止めない（白画面化させない方針と同じ）。
// ============================================================

import { create } from 'zustand'
import {
  CURRENT_APP_BUILD_ID,
  StaleAppVersionError,
  fetchLatestAppVersion,
  getAppVersionStatus,
} from '@/lib/appVersion'
import {
  isMutatingMethod,
  shouldCheckAppVersion,
  shouldWarnForConsecutiveUnknown,
  type AppVersionInfo,
  type AppVersionStatus,
} from '@/lib/appVersionCore'

// dev / build id 未設定では guard を一切働かせない（fetch もしない）
const GUARD_DISABLED = !CURRENT_APP_BUILD_ID

interface AppVersionState {
  status: AppVersionStatus | null
  lastCheckedAt: number | null
  consecutiveUnknown: number
  /** 書き込み前ガードが stale を検知して表示するブロックモーダルの可視状態 */
  blocking: boolean
  /** 読み取りバナーを「閉じる」した対象の最新 build id（同じ版の間は再表示しない） */
  dismissedBuildId: string | null
  /** 同時呼び出しで /app-version.json を多重 fetch しないための in-flight 共有 */
  inFlight: Promise<AppVersionStatus> | null

  /** /app-version.json を取得し状態を更新。force=false は TTL 内ならキャッシュを返す（毎 write で 2 往復しない）。 */
  check: (force?: boolean) => Promise<AppVersionStatus>
  /** mutating メソッドの直前に呼ぶ。stale ならブロックモーダルを出し StaleAppVersionError を投げる。 */
  ensureFreshForMutation: (method?: string | null) => Promise<void>
  /** 読み取りバナーを現在の最新 build id について閉じる。 */
  dismissBanner: () => void
  /** 最新版へ更新（同一 URL で fresh load）。 */
  reloadNow: () => void
}

export const useAppVersionStore = create<AppVersionState>((set, get) => ({
  status: null,
  lastCheckedAt: null,
  consecutiveUnknown: 0,
  blocking: false,
  dismissedBuildId: null,
  inFlight: null,

  check: async (force = false) => {
    // dev / build id 未設定: 常に unknown を返し fetch しない（guard 無効）
    if (GUARD_DISABLED) {
      const status = getAppVersionStatus(null)
      set({ status, lastCheckedAt: Date.now() })
      return status
    }

    const { lastCheckedAt, inFlight, status: cached } = get()

    // TTL 内かつ前回結果があれば再 fetch しない（書き込み前の連続呼び出しを直列 2 往復にしない）
    if (cached && !shouldCheckAppVersion({ now: Date.now(), lastCheckedAt, force })) {
      return cached
    }
    // 同時呼び出しは in-flight promise を共有
    if (inFlight) return inFlight

    const promise = (async (): Promise<AppVersionStatus> => {
      let latest: AppVersionInfo | null = null
      try {
        latest = await fetchLatestAppVersion()
      } catch {
        // 通信失敗 → unknown 扱い（一時的な失敗で操作を止めない）
        latest = null
      }
      const status = getAppVersionStatus(latest)
      const consecutiveUnknown = status.state === 'unknown' ? get().consecutiveUnknown + 1 : 0
      if (status.state === 'unknown' && shouldWarnForConsecutiveUnknown(consecutiveUnknown)) {
        // 版差分チェック機構自体が死んでいる可能性に気づけるようにする
        console.warn(
          `[appVersion] /app-version.json を ${consecutiveUnknown} 回連続で取得できません。バージョン確認が機能していない可能性があります。`,
        )
      }
      set({ status, lastCheckedAt: Date.now(), consecutiveUnknown, inFlight: null })
      return status
    })()

    set({ inFlight: promise })
    return promise
  },

  ensureFreshForMutation: async (method) => {
    if (GUARD_DISABLED) return
    if (!isMutatingMethod(method)) return
    const status = await get().check(false) // TTL キャッシュ利用
    if (status.state === 'stale') {
      set({ blocking: true })
      throw new StaleAppVersionError(status.currentBuildId, status.latestBuildId ?? '')
    }
  },

  dismissBanner: () => {
    set({ dismissedBuildId: get().status?.latestBuildId ?? null })
  },

  reloadNow: () => {
    window.location.reload()
  },
}))
